import {
  CalculateDiscountCommands,
  CalculateDiscountResult,
  DiscountApplication,
  DiscountType,
} from '../types/discounts.types';
import { Big, bigSum } from './big-helpers';

/**
 * Whether a discount is applied by the cashier at checkout rather than
 * automatically to its products. Rows without the field (created before
 * manual discounts existed) are automatic.
 */
export function isManualDiscount(discount: {
  application?: DiscountApplication | null;
}): boolean {
  return discount.application === DiscountApplication.MANUAL;
}

/**
 * The cashier picks a manual discount from predefined values only: variable
 * and points-based types have no value to apply and never reach the POS.
 */
export const MANUAL_DISCOUNT_TYPES: readonly DiscountType[] = [
  DiscountType.FIXED_PERCENT,
  DiscountType.FIXED_AMOUNT,
];

/**
 * Whether a discount's deadline has passed. A missing, null, or unparsable
 * valid_until means the discount never expires.
 */
export function isDiscountExpired(
  discount: { valid_until?: string | null },
  at: Date = new Date(),
): boolean {
  if (!discount.valid_until) return false;
  const deadline = Date.parse(discount.valid_until);
  if (Number.isNaN(deadline)) return false;
  return deadline <= at.getTime();
}

/**
 * Whether changing a discount deadline reactivates an expired discount.
 * Removing a deadline is deliberately not this transition: reactivation here
 * means that the replacement deadline is explicitly later than `at`.
 */
export function isDiscountReactivation(
  previous: { valid_until?: string | null },
  nextValidUntil: string | null | undefined,
  at: Date = new Date(),
): boolean {
  if (!isDiscountExpired(previous, at) || !nextValidUntil) return false;
  const nextDeadline = Date.parse(nextValidUntil);
  return !Number.isNaN(nextDeadline) && nextDeadline > at.getTime();
}

export class DiscountCalculator {
  // Method to calculate percentage discount
  static calculatePercentageDiscount(
    itemPrice: number,
    discountPercent: number,
  ): CalculateDiscountResult {
    const price = new Big(itemPrice);
    const discountValue = price.times(new Big(discountPercent).div(100));
    const finalPrice = price.minus(discountValue);
    return {
      discount_command: CalculateDiscountCommands.PERCENTAGE,
      item_price: itemPrice,
      final_price: finalPrice.round(2).toNumber(),
      discount_value: discountValue.round(2).toNumber(),
    };
  }

  // Method to calculate fixed amount discount
  static calculateFixedDiscount(
    itemPrice: number,
    discountAmount: number,
  ): CalculateDiscountResult {
    const finalPrice = new Big(itemPrice).minus(discountAmount);
    return {
      discount_command: CalculateDiscountCommands.FIXED,
      item_price: itemPrice,
      final_price: finalPrice.round(2).toNumber(),
      discount_value: new Big(discountAmount).round(2).toNumber(),
    };
  }

  // Method to calculate multiple discounts (percentage + fixed)
  static calculateMultipleDiscounts(
    itemPrice: number,
    discountPercent: number,
    discountAmount: number,
  ): CalculateDiscountResult {
    const price = new Big(itemPrice);
    const percentageDiscount = price.times(new Big(discountPercent).div(100));
    const totalDiscount = percentageDiscount.plus(discountAmount);
    const finalPrice = price.minus(totalDiscount);
    return {
      discount_command: CalculateDiscountCommands.MULTIPLE_DISCOUNTS,
      item_price: itemPrice,
      final_price: finalPrice.round(2).toNumber(),
      discount_value: totalDiscount.round(2).toNumber(),
      total_discount: totalDiscount.round(2).toNumber(),
    };
  }

  // Method to apply item-level discounts
  static calculateItemLevelDiscounts(
    items: { price: number; discountPercent: number }[],
  ): CalculateDiscountResult {
    const discountItems = items.map((item) => {
      const price = new Big(item.price);
      const discountValue = price.times(new Big(item.discountPercent).div(100));
      const finalPrice = price.minus(discountValue);
      return {
        price: item.price,
        finalPrice: finalPrice.round(2).toNumber(),
        discount_value: discountValue.round(2).toNumber(),
        discount_ids: [],
      };
    });
    return {
      discount_command: CalculateDiscountCommands.ITEM_LEVEL_DISCOUNT,
      items: discountItems,
    };
  }

  // Method to calculate receipt-level discount
  static calculateReceiptLevelDiscount(
    items: { price: number }[],
    discountPercent: number,
  ): CalculateDiscountResult {
    const totalOriginalPrice = bigSum(items.map((item) => item.price));
    const discountValue = totalOriginalPrice.times(
      new Big(discountPercent).div(100),
    );
    const finalPrice = totalOriginalPrice.minus(discountValue);
    return {
      discount_command: CalculateDiscountCommands.RECEIPT_LEVEL_DISCOUNT,
      item_price: totalOriginalPrice.round(2).toNumber(),
      final_price: finalPrice.round(2).toNumber(),
      discount_value: discountValue.round(2).toNumber(),
      total_discount: discountValue.round(2).toNumber(),
    };
  }

  // Method to apply tax after discounts have been applied
  static calculateTaxAfterDiscount(
    finalPrice: number,
    taxRate: number,
  ): CalculateDiscountResult {
    const price = new Big(finalPrice);
    const taxValue = price.times(new Big(taxRate).div(100));
    const totalPriceWithTax = price.plus(taxValue);
    return {
      discount_command: CalculateDiscountCommands.TAX_AFTER_DISCOUNT,
      item_price: finalPrice,
      final_price: totalPriceWithTax.round(2).toNumber(),
      total_tax: taxValue.round(2).toNumber(),
    };
  }

  // Method to apply tax before discounts
  static calculateTaxBeforeDiscount(
    itemPrice: number,
    taxRate: number,
    discountPercent: number,
  ): CalculateDiscountResult {
    const price = new Big(itemPrice);
    const taxValue = price.times(new Big(taxRate).div(100));
    const priceWithTax = price.plus(taxValue);
    const discountValue = priceWithTax.times(new Big(discountPercent).div(100));
    const finalPrice = priceWithTax.minus(discountValue);
    return {
      discount_command: CalculateDiscountCommands.TAX_BEFORE_DISCOUNT,
      item_price: itemPrice,
      final_price: finalPrice.round(2).toNumber(),
      discount_value: discountValue.round(2).toNumber(),
      total_tax: taxValue.round(2).toNumber(),
    };
  }
}
